import { useCallback, useEffect, useRef, useState } from 'react';
import { jamApi, SERVER_URL } from '../lib/jamSocket.js';

/*
 * Motor de reproduccion de audio.
 *
 * Responsabilidades:
 *  1. Cargar el stream de la pista en curso en un <audio> oculto. El src es
 *     el RELAY del backend (/api/stream/:videoId, ver http/streamRoute.js):
 *     el <audio> jamas habla con googlevideo, el backend transmite el audio
 *     (evita los 403 de navegador contra URLs directos en algunas sesiones).
 *  2. Mantener sincronia con el reloj del servidor: playback.positionSec
 *     interpolado localmente y correccion de deriva >= 2 s en marcha
 *     (>= 0.4 s en pausa: los seeks congelados deben verse ya).
 *  3. Resiliencia: si el <audio> falla, avisa por `stream:error` (el backend
 *     re-resuelve) y recarga elrelay con el `exp=` nuevo; dedupe de dobles
 *     recargas (ack + broadcast `stream:refresh` que llegan juntos).
 *  4. Reconciliacion al terminar pista: el servidor auto-avanza con su tick;
 *     solo si es el anfitrion y el servidor NO avanzo, ordena 'next'.
 *  5. Prefetch: calienta el cache del servidor con la siguiente pista.
 *  6. Politica de autoplay: si el navegador bloquea play(), expone
 *     `needGesture` para overlay "Toca para entrar".
 */

const DRIFT_LIMIT_PLAYING_SEC = 1;
const DRIFT_LIMIT_PAUSED_SEC = 0.4;
// El primer play de una pista SIN cachear espera a que el servidor la baje
// con yt-dlp (7-25 s tipico). Margen amplio para no pintar error mientras
// el relay trabaja; las pistas ya cacheadas cargan al instante.
const LOAD_TIMEOUT_MS = 60_000;
const RELOAD_GUARD_MS = 800; // dedupe ack(stream:error) + broadcast(stream:refresh)
const RETRY_SAME_SRC_MS = 10_000; // no reintentar en bucle el mismo src fallido
const URL_TAIL = 40;
const URL_CACHE_MAX = 40;

/**
 * src que recibe el <audio>: el RELAY del backend (/api/stream/:videoId) en
 * lugar del URL de googlevideo directamente. Razon: los URLs son servibles
 * desde Node (206 verificado) pero algunos navegadores/sesiones reciben 403
 * por el mismo URL; via backend el media element habla con el mismo origen
 * del servidor, sin cookies ni QUIC de YouTube. El `exp` (epoch del expire)
 * va en query para que un re-resolve produzca un tail distinto y la
 * dedupe del motor permita recargar sin cambiar de pista.
 */
function streamSrc(videoId, expiresAt) {
  const expMs = Number(expiresAt);
  const expSec = Math.floor((Number.isFinite(expMs) && expMs > Date.now() ? expMs : Date.now() + 300_000) / 1000);
  return `${SERVER_URL}/api/stream/${videoId}?exp=${expSec}`;
}

/**
 * @param {object} jam      contexto de useJamRoom (se lee siempre via ref fresca)
 * @param {React.RefObject<HTMLAudioElement>} audioRef
 */
export function usePlaybackEngine(jam, audioRef) {
  const { playback, currentTrack, streamRefresh } = jam;

  /** idle | loading | ready | error */
  const [engineState, setEngineState] = useState('idle');
  const [needGesture, setNeedGesture] = useState(false);

  /* Snapshot del contexto actualizado en cada render: los efectos/async leen
   * siempre sus valores actuales sin retriggerear efectos. */
  const jamRef = useRef(jam);
  jamRef.current = jam;

  const seqRef = useRef(0); // orden de prioridad de cargas (solo la ultima gana)
  const busyRef = useRef(false); // hay carga de pista en curso: el sync no toca
  /** ultimo src aplicado: { videoId, url, at } (dedupe dobles recargas) */
  const appliedRef = useRef(null);
  const failRef = useRef({ tail: null, at: 0 });
  /** cache cliente de URLs: videoId -> { url, expiresAt } */
  const urlCacheRef = useRef(new Map());

  const cachePut = useCallback((videoId, url, expiresAt) => {
    const cache = urlCacheRef.current;
    cache.set(videoId, { url, expiresAt: expiresAt || Date.now() + 5 * 60_000 });
    if (cache.size > URL_CACHE_MAX) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
  }, []);

  const cacheGet = useCallback((videoId) => {
    const hit = urlCacheRef.current.get(videoId);
    if (!hit || hit.expiresAt - Date.now() <= 30_000) return null;
    return hit; // { url, expiresAt }  (url es el de googlevideo, solo informativo)
  }, []);

  /** Warming del cache del servidor + { url, expiresAt } para el src relay. */
  const fetchStreamUrl = useCallback(async (videoId, { force = false } = {}) => {
    if (!force) {
      const cached = cacheGet(videoId);
      if (cached) return cached;
    }
    // ack: { ok, videoId, url, expiresAt, cache: 'hit'|'miss' }
    const res = await jamApi.getStreamUrl({ videoId });
    cachePut(videoId, res.url, res.expiresAt);
    return { url: res.url, expiresAt: res.expiresAt };
  }, [cacheGet, cachePut]);

  /** Estado objetivo interpolado (reloj servidor + delta local). */
  const targetNow = useCallback(() => {
    return jamRef.current.getPlaybackNow?.() ?? null;
  }, []);

  const playSilent = useCallback((audio) => {
    audio.play().catch((err) => {
      if (err?.name === 'NotAllowedError') setNeedGesture(true);
      // AbortError: el src se reemplazo a mitad de arranque; es normal.
    });
  }, []);

  /* ── Aplicacion de un URL al elemento <audio> ─────────────────────────── */

  const applyUrl = useCallback(
    (url, { videoId = null, positionSec = 0, playing = false, freshStart = false } = {}) => {
      const audio = audioRef.current;
      if (!audio || typeof url !== 'string' || !url) return;

      const at = Date.now();
      const prevTail = String(audio.currentSrc || '').slice(-URL_TAIL);
      // Dedupe: mismo src aplicado hace < 800 ms (ack + broadcast gemelos).
      if (prevTail && prevTail === url.slice(-URL_TAIL) && at - (appliedRef.current?.at ?? 0) < RELOAD_GUARD_MS) {
        return;
      }
      appliedRef.current = { videoId, url, at };
      setEngineState('loading');
      setNeedGesture(false);

      let settled = false;
      const srcReady = () => {
        if (settled) return;
        settled = true;
        cleanup();
        const fresh = jamRef.current.getPlaybackNow?.();
        if (freshStart && jamRef.current.isHost) {
          // Pista recien arrancada (posicion ~0) y el primer play puede tardar
          // (descarga yt-dlp: 7-25 s). El anfitrion es la fuente de verdad:
          // arranca desde 0 y re-ancla el reloj de la sala a su audio, en vez
          // de empezar 15-20 s avanzado por la espera de la descarga.
          try {
            audio.currentTime = 0;
          } catch {
            /* noop */
          }
          if (playing) playSilent(audio);
          const sync = jamRef.current.syncPlayback;
          if (sync) sync({ playing: true, positionSec: 0 }).catch(() => {});
        } else {
          // Re-ancla a la posicion INTERPOLADA de AHORA (late join / recarga):
          // usar la posicion capturada antes de la carga dejaria la sala atras.
          const startSec = Math.max(0, fresh?.positionSec ?? positionSec);
          try {
            audio.currentTime = startSec;
          } catch {
            /* si la metadata no llego, el sync por deriva lo corrige */
          }
          if (fresh?.playing ?? playing) playSilent(audio);
        }
        setEngineState('ready');
      };
      // Los fallos de carga los atiende el listener global de 'error'
      // (recuperacion con stream:error); aqui solo cerramos el timeout.
      const srcError = () => {
        if (settled) return;
        settled = true;
        cleanup();
      };
      const cleanup = () => {
        clearTimeout(timer);
        audio.removeEventListener('loadedmetadata', srcReady);
        audio.removeEventListener('error', srcError);
      };
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        setEngineState('error');
      }, LOAD_TIMEOUT_MS);

      audio.preload = 'auto';
      audio.src = url;
      audio.load();
      audio.addEventListener('loadedmetadata', srcReady);
      audio.addEventListener('error', srcError);
    },
    [audioRef, playSilent],
  );

  /* ── Recuperacion de stream caido (listener global de 'error') ─────────── */

  const recoverStream = useCallback(async () => {
    const audio = audioRef.current;
    const now = jamRef.current;
    const videoId = now.currentTrack?.videoId;
    if (!audio || !videoId) return;

    const tail = String(audio.currentSrc || '').slice(-URL_TAIL);
    const stamp = Date.now();
    // No reintentar el mismo src cascado en bucle.
    if (failRef.current.tail === tail && stamp - failRef.current.at < RETRY_SAME_SRC_MS) return;
    failRef.current = { tail, at: stamp };
    // Doble-recarga: demasiado pronto tras aplicar (ack + broadcast gemelos).
    if (stamp - (appliedRef.current?.at ?? 0) < RELOAD_GUARD_MS) return;

    setEngineState('loading');
    try {
      // El backend invalida su cache, re-resuelve y difunde `stream:refresh`
      // a la sala; a NOSOTROS devuelve el URL fresco por ack.
      const res = await now.reportStreamError({
        videoId,
        code: audio.error?.code === 4 ? 'src_not_supported' : 'media_error',
      });
      if (res?.url) cachePut(videoId, res.url, res.expiresAt);
      if (now.currentTrack?.videoId !== videoId) return; // ya hubo salto de pista
      const info = res?.url
        ? { url: res.url, expiresAt: res.expiresAt }
        : await fetchStreamUrl(videoId);
      const target = targetNow();
      const seq = ++seqRef.current; // invalida cualquier carga anterior
      busyRef.current = false;
      const freshSrc = streamSrc(videoId, info?.expiresAt);
      failRef.current = { tail: String(freshSrc).slice(-URL_TAIL), at: Date.now() };
      applyUrl(freshSrc, {
        videoId,
        positionSec: target?.positionSec ?? 0,
        playing: target?.playing ?? true,
      });
    } catch (err) {
      jamRef.current.pushToast?.(err?.message || 'No se pudo recuperar el audio', 'error');
      setEngineState('error');
    }
  }, [applyUrl, audioRef, cachePut, fetchStreamUrl, targetNow]);

  /* ── Fin de pista: reconciliacion con el auto-avance del servidor ─────── */

  const onEnded = useCallback(() => {
    const now = jamRef.current;
    if (!now.isHost) return; // oyentes: el tick del servidor difunde el salto
    const lastIndex = (now.queue?.length ?? 0) - 1;
    if (lastIndex < 0) return;
    (async () => {
      const res = await now.syncRequest().catch(() => null);
      if (!res?.playback) return;
      // Si el servidor YA avanzo, su broadcast aplicara el nuevo estado.
      if (res.playback.currentIndex === now.currentIndex && now.currentIndex < lastIndex) {
        now.control('next').catch(() => {}); // el provider toastea 'no_more_tracks'
      }
    })();
  }, []);

  /* ── Listeners globales del elemento de audio ────────────────────────── */

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onPlaying = () => setNeedGesture(false);
    audio.addEventListener('error', recoverStream);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('playing', onPlaying);
    return () => {
      audio.removeEventListener('error', recoverStream);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('playing', onPlaying);
    };
  }, [audioRef, recoverStream, onEnded]);

  /* ── [1] Carga de pista nueva (por cambio de videoId) ─────────────────── */

  const videoId = currentTrack?.videoId ?? null;

  useEffect(() => {
    const audio = audioRef.current;
    if (!videoId) {
      if (audio) audio.pause(); // cola vacia / fuera de sala
      return undefined;
    }
    let cancelled = false;
    const seq = ++seqRef.current;
    setEngineState('loading');
    // Al cambiar de pista (next/prev/jump) cortamos la anterior de inmediato:
    // no debe seguir sonando mientras carga la nueva (p. ej. primer play con
    // descarga yt-dlp lenta). La nueva arranca re-anclada al reloj del server.
    if (audio && appliedRef.current?.videoId !== videoId) {
      audio.pause();
    }

    (async () => {
      busyRef.current = true;
      try {
        // Warming del cache del servidor + expiresAt para el src del relay.
        const info = await fetchStreamUrl(videoId);
        if (cancelled || seqRef.current !== seq) return;
        const target = targetNow();
        const targetPos = target?.positionSec ?? 0;
        applyUrl(streamSrc(videoId, info?.expiresAt), {
          videoId,
          positionSec: targetPos,
          playing: target?.playing ?? true,
          // Arranque limpio de pista (no late-join): el host puede re-anclar
          // el reloj a su audio si la primera carga se demoro (yt-dlp).
          freshStart: targetPos < 2 && (target?.playing ?? false),
        });
        busyRef.current = false;
        // Prefetch de la siguiente URL (calienta cache cliente y servidor).
        const next = jamRef.current.queue?.[(jamRef.current.currentIndex ?? -1) + 1];
        if (next?.videoId) fetchStreamUrl(next.videoId).catch(() => {});
      } catch (err) {
        if (cancelled || seqRef.current !== seq) return;
        busyRef.current = false;
        jamRef.current.pushToast?.(err?.message || 'No se pudo cargar la canción', 'error');
        setEngineState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [videoId, applyUrl, fetchStreamUrl, targetNow, audioRef]);

  /* ── [2] Sync continuo: play/pause, seeks y correccion de deriva ──────── */

  /* Se ejecuta al recibir cada broadcast Y cada segundo mientras suena: usa
   * el reloj INTERPOLADO (getPlaybackNow), no el `positionSec` congelado del
   * ultimo snapshot. Asi la deriva se corrige en ~1 s, no al siguiente
   * `playback:state` (hasta 30 s), y una pista recien cargada arranca en el
   * punto exacto de la sala. */
  const syncToServer = useCallback(() => {
    const audio = audioRef.current;
    const now = jamRef.current;
    const target = now.getPlaybackNow?.();
    if (!audio || !target) return;
    const track = now.queue?.[target.index ?? -1];
    // Solo tocamos el elemento si corresponde a la pista ya aplicada.
    if (!track || appliedRef.current?.videoId !== track.videoId) return;
    if (busyRef.current) return; // una carga en curso fijara su posicion exacta

    const position = target.positionSec ?? 0;
    if (target.playing) {
      if (audio.paused) playSilent(audio);
      const drift = audio.currentTime - position;
      if (!audio.paused && audio.duration > 0 && Math.abs(drift) > DRIFT_LIMIT_PLAYING_SEC) {
        try {
          audio.currentTime = position;
        } catch {
          /* metadata no lista; el siguiente tick corregira */
        }
      }
    } else {
      if (!audio.paused) audio.pause();
      if (Math.abs(audio.currentTime - position) > DRIFT_LIMIT_PAUSED_SEC) {
        try {
          audio.currentTime = position;
        } catch {
          /* noop */
        }
      }
    }
  }, [audioRef, playSilent]);

  useEffect(() => {
    if (!playback) return undefined;
    syncToServer(); // al llegar el broadcast
    if (!playback.playing) return undefined;
    const id = setInterval(syncToServer, 1_000); // deriva continua del reloj
    return () => clearInterval(id);
  }, [playback, syncToServer]);

  /* ── [3] `stream:refresh` (otro cliente refresco un enlace) ───────────── */

  useEffect(() => {
    if (!streamRefresh?.url) return;
    const now = jamRef.current;
    const audio = audioRef.current;
    const videoIdNow = now.currentTrack?.videoId;
    if (!audio || !videoIdNow || streamRefresh.videoId !== videoIdNow) return;

    const freshSrc = streamSrc(videoIdNow, streamRefresh.expiresAt);
    if (String(audio.currentSrc || '').slice(-URL_TAIL) === freshSrc.slice(-URL_TAIL)) return; // ya aplicado
    if (Date.now() - (appliedRef.current?.at ?? 0) < RELOAD_GUARD_MS) return; // ack gemelo ya aplico

    cachePut(videoIdNow, streamRefresh.url, streamRefresh.expiresAt);
    busyRef.current = false;
    const seq = ++seqRef.current;
    const target = targetNow();
    applyUrl(freshSrc, {
      videoId: videoIdNow,
      positionSec: target?.positionSec ?? 0,
      playing: target?.playing ?? true,
    });
  }, [streamRefresh, applyUrl, cachePut, targetNow, audioRef]);

  /* ── API para la UI ───────────────────────────────────────────────────── */

  /** Click en el overlay anti-autoplay: recoloca y reanuda con gesto. */
  const resumeFromGesture = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const target = targetNow();
    if (target?.playing) {
      try {
        audio.currentTime = target.positionSec;
      } catch {
        /* noop */
      }
    }
    audio.play().then(() => setNeedGesture(false)).catch(() => {});
  }, [audioRef, targetNow]);

  /** Reintentar tras error: pide re-resolve forzado (stream:error) y aplica. */
  const retryLoad = useCallback(() => {
    const videoIdNow = jamRef.current.currentTrack?.videoId;
    if (!videoIdNow) return;
    const target = targetNow();
    (async () => {
      setEngineState('loading');
      try {
        const res = await jamRef.current.reportStreamError({ videoId: videoIdNow, code: 'manual_retry' });
        if (res?.url) cachePut(videoIdNow, res.url, res.expiresAt);
        const info = res?.url
          ? { url: res.url, expiresAt: res.expiresAt }
          : await fetchStreamUrl(videoIdNow);
        applyUrl(streamSrc(videoIdNow, info?.expiresAt), {
          videoId: videoIdNow,
          positionSec: target?.positionSec ?? 0,
          playing: target?.playing ?? true,
        });
      } catch (err) {
        jamRef.current.pushToast?.(err?.message || 'No se pudo reintentar el audio', 'error');
        setEngineState('error');
      }
    })();
  }, [applyUrl, cachePut, fetchStreamUrl, targetNow]);

  return { engineState, needGesture, resumeFromGesture, retryLoad };
}
